import type { ConnectionSetupOptions } from '@lobu/core/contracts/tools/manage-connections';
import { useConnectionSetupOptions } from '@/lib/api/connections';
import { Button } from '@/components/ui/button';

export function SetupOptions({ data, error, retry }: { data?: ConnectionSetupOptions; error?: boolean; retry?: () => void }) {
  const choices = data?.options.filter(option => option.kind !== 'local' && option.configured && !!option.url) ?? [];
  const unavailable = error || data?.cloud_status === 'unavailable';
  if (!choices.length && !unavailable) return null;
  return <section aria-label="Available connection options" className="space-y-3">
    {unavailable && <div role="status" className="text-sm text-muted-foreground">
      <p>Cloud connection options could not be checked. You can retry or continue with your own app.</p>
      {retry && <Button variant="link" className="px-0" onClick={retry}>Retry cloud discovery</Button>}
    </div>}
    {choices.map(option => <div key={`${option.kind}:${option.managed_by_org ?? option.url}`} className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        {option.url && <Button asChild variant="outline"><a href={option.url} target="_blank" rel="noopener noreferrer">{option.label}</a></Button>}
        <span className="text-xs text-muted-foreground">{option.execution === 'cloud' ? 'Runs in Lobu Cloud' : 'Available for local execution'}</span>
      </div>
      <p className="text-sm text-muted-foreground">{option.description}</p>
      <details className="text-xs text-muted-foreground"><summary className="cursor-pointer">Setup steps</summary><p className="pt-1">{option.instructions}</p></details>
    </div>)}
  </section>;
}

export function ConnectionSetupChoices({ organizationId, connectorKey }: { organizationId: string; connectorKey: string }) {
  const query = useConnectionSetupOptions(organizationId, connectorKey);
  if (!organizationId || query.isPending) return <p role="status" className="text-sm text-muted-foreground">Checking connection options…</p>;
  return <SetupOptions data={query.data} error={query.isError} retry={() => void query.refetch()} />;
}
